import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import AppUtilities from "@toothfairy/shared-ui/AppUtilities";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import requestLogsHelper from "../../../API/RequestLogs/requestLogsHelper";
import workspacesHelper from "../../../API/Workspaces/workspacesHelper";
import useWorkspaces from "../../../Hooks/useWorkspaces";
import SupportView from "../Components/SupportView";

const SupportContainer = () => {
  const { user } = AppUser.useUser();
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const { getActiveWorkspace, updateWorkspaceInContext, isToothFairyStaff } =
    useWorkspaces();
  const workspace = getActiveWorkspace();
  const canLogSupport = isToothFairyStaff();
  const [logs, setLogs] = useState([]);
  const [isLogsLoading, setIsLogsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadLogs = useCallback(async () => {
    if (!workspace?.id) return;
    setIsLogsLoading(true);
    try {
      const result =
        await requestLogsHelper.getAllRequestLogsByWorkspaceAndType(
          workspace.id,
          "support"
        );
      setLogs(result?.filter((u) => u?._deleted !== true) || []);
    } catch (error) {
      console.error("An error occurred while loading support logs", error);
    } finally {
      setIsLogsLoading(false);
    }
  }, [workspace?.id]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const adjustWorkspaceUsage = async (delta) => {
    if (!workspace?.id) return null;
    const updatedWorkspace = await workspacesHelper.updateWorkspace(
      workspace.id,
      [
        {
          property: "supportHoursUsed",
          value: Math.max(0, (workspace.supportHoursUsed || 0) + delta),
        },
      ]
    );
    updateWorkspaceInContext(updatedWorkspace);
    return updatedWorkspace;
  };

  const handleSubmit = async ({ supportHours, description, activityDate }) => {
    if (!workspace?.id || !isToothFairyStaff()) return;
    setIsSubmitting(true);
    try {
      const result = await requestLogsHelper.createSupportLog({
        workspaceID: workspace.id,
        supportHours,
        description,
        userID: user?.id,
        createdBy: user?.user_email,
        creationTime: activityDate
          ? Math.floor(dayjs(activityDate).valueOf() / 1000)
          : Math.floor(dayjs().valueOf() / 1000),
      });
      if (result?.data?.createRequestLog) {
        await adjustWorkspaceUsage(supportHours);
        AppToasty.show(t("admin.support.successCaption"), {
          type: "success",
        });
      }
      loadLogs();
    } catch (error) {
      console.error("An error occurred while logging support hours", error);
      AppToasty.show(t("admin.support.failureCaption"), {
        type: "danger",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteLog = async (log) => {
    if (!log?.id || !workspace?.id || !isToothFairyStaff()) return;
    setIsDeleting(true);
    try {
      const result = await requestLogsHelper.deleteSupportLog({
        id: log.id,
        _version: log._version,
      });
      if (result?.data?.deleteRequestLog) {
        await adjustWorkspaceUsage(-(log?.supportHours || 0));
        AppToasty.show(t("admin.support.deleteSuccessCaption"), {
          type: "success",
        });
      }
      loadLogs();
    } catch (error) {
      console.error("An error occurred while deleting support entry", error);
      AppToasty.show(t("admin.support.deleteFailureCaption"), {
        type: "danger",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const formattedLogs = [...logs]
    .sort((a, b) => (b?.creationTime || 0) - (a?.creationTime || 0))
    .map((log) => ({
      datetime: AppUtilities.dateConverter.ConvertTimeStampToDateString(
        log.creationTime * 1000,
        "en",
        t,
        true
      ).toString(),
      engineer: log.createdBy || log.userID || "—",
      hours: log.supportHours ?? log.words ?? "—",
      description: log.description || "—",
      log,
    }));

  const totalUsedHours = logs.reduce(
    (sum, log) => sum + (log?.supportHours || 0),
    0
  );

  return (
    <SupportView
      workspace={workspace}
      logs={formattedLogs}
      usedHours={totalUsedHours}
      isLogsLoading={isLogsLoading}
      isSubmitting={isSubmitting}
      isDeleting={isDeleting}
      canLogSupport={canLogSupport}
      onSubmit={handleSubmit}
      onDeleteLog={handleDeleteLog}
    />
  );
};

export default SupportContainer;